/* eslint-disable @reatom/unit-naming-rule */
import { action, sleep } from '@reatom/framework'

import { Duration, Result } from '@blade/std-lib'
import { UnknownError } from '@root/shared/core'
import { TimeoutError } from '@root/shared/core/Error/Error'

import { workerAtom } from './reatom-worker'

import type { ClientToWorkerMessage } from '../model/client-to-worker-message'
import type { WorkerToClientMessage } from '../model/worker-to-client-message'

export const createRpc = <
  RequestType extends ClientToWorkerMessage['type'],
  ResponseType extends WorkerToClientMessage['type'],
>(args: {
  requestType: RequestType
  responseType: ResponseType
}) => {
  const { requestType, responseType } = args

  type Request = ClientToWorkerMessage & {
    type: RequestType
  }
  type Response = WorkerToClientMessage & {
    type: ResponseType
  }
  return action(
    async (
      ctx,
      data: Omit<Request, 'type'>,
      timeout: Duration.Duration = Duration.seconds(10),
    ): Promise<Result.Result<Response, UnknownError | TimeoutError>> => {
      performance.measure(`rpc-${requestType}`)
      const worker = ctx.get(workerAtom)
      if (!worker) {
        return Result.fail(UnknownError.fromUnknown('no worker'))
      }
      let resolve: (value: Response) => void
      const handler = (event: MessageEvent) => {
        const message = event.data as WorkerToClientMessage
        if (message.type === responseType) {
          worker.port.removeEventListener('message', handler)
          resolve(message as Response)
        }
      }
      const workerPromise = new Promise<Response>((res) => {
        resolve = res
        worker.port.addEventListener('message', handler)
      })

      const message = {
        type: requestType,
        ...data,
      } as Request

      worker.port.postMessage(message)
      const timeoutPromise = sleep(Duration.toMillis(timeout)).then(
        () => new TimeoutError(),
      )

      const result = await ctx.schedule(() =>
        Promise.race([workerPromise, timeoutPromise]),
      )
      if (result instanceof TimeoutError) {
        return Result.fail(result)
      }
      return Result.succeed(result)
    },
  )
}

export const createSendAction = <
  RequestType extends ClientToWorkerMessage['type'],
>(args: {
  requestType: RequestType
}) => {
  type Request = ClientToWorkerMessage & {
    type: RequestType
  }
  const { requestType } = args
  return action((ctx, data: Omit<Request, 'type'>) => {
    const worker = ctx.get(workerAtom)
    if (!worker) {
      return
    }

    worker.port.postMessage({
      type: requestType,
      ...data,
    })
  })
}
